"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center bg-muted/30 border border-border rounded-md shadow-sm">
        <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
          <svg className="size-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-default font-bold text-foreground mb-2">
          Inquiry Received
        </h3>
        <p className="text-muted-foreground font-default mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary hover:bg-accent">
          Submit New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Officer or Citizen Name" className="rounded-md border-input focus-visible:ring-ring focus-visible:border-primary bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Official Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Official Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@department.gov" className="rounded-md border-input focus-visible:ring-ring focus-visible:border-primary bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{}} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-foreground">Contact Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 000-0000" className="rounded-md border-input focus-visible:ring-ring focus-visible:border-primary bg-background" {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs text-muted-foreground">
              Optional: For direct follow-up regarding your inquiry.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Inquiry details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-foreground">Inquiry Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Please describe the nature of your request or report..." className="min-h-[150px] rounded-md border-input focus-visible:ring-ring focus-visible:border-primary bg-background resize-y" {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs text-muted-foreground">
              Provide as much detail as possible for efficient processing.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-default font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-md h-12 text-base font-default font-bold uppercase tracking-wider">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : "Submit Official Inquiry"}
      </Button>
    </Form>;
}
export default function ServiceInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section data-section-id="234615" id="service-inquiry" className="w-full bg-background text-foreground py-24 px-6 md:px-12 lg:px-24">
      <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl leading-tight font-default font-extrabold text-primary mb-4 uppercase tracking-tight">
            Official Inquiry
          </h2>
          <p className="text-lg font-default text-muted-foreground max-w-xl mx-auto leading-relaxed">
            The Metropolitan Central Police Department is committed to transparency and public safety. 
            Submit your formal request or inquiry below for departmental review.
          </p>
        </div>

        <div className="bg-card p-8 md:p-10 border border-border shadow-lg rounded-lg">
          <FormProvider formKey="service_inquiry_public_services" formId="service_inquiry_public_services" sectionName="Service Inquiry" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false
          },
          message: {
            required: true
          }
        }} submitText="Submit Official Inquiry" successMessage="Your inquiry has been logged in our system. A representative will review your request shortly.">
            <ContactFormInner />
          </FormProvider>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 bg-muted rounded-md border border-border shadow-sm text-center">
            <h4 className="font-default font-bold text-primary mb-1">Records Division</h4>
            <p className="text-sm font-mono text-muted-foreground">EXT: 402</p>
          </div>
          <div className="p-6 bg-muted rounded-md border border-border shadow-sm text-center">
            <h4 className="font-default font-bold text-primary mb-1">Community Affairs</h4>
            <p className="text-sm font-mono text-muted-foreground">EXT: 515</p>
          </div>
          <div className="p-6 bg-muted rounded-md border border-border shadow-sm text-center">
            <h4 className="font-default font-bold text-primary mb-1">Public Information</h4>
            <p className="text-sm font-mono text-muted-foreground">EXT: 109</p>
          </div>
        </div>
      </div>
    </section>;
}